import {Box, styled, Typography, Skeleton, useTheme} from "@mui/material";
import {LineChart} from "@mui/x-charts/LineChart";
import {useChart} from "../../hooks";
import {useTeamProgressContext} from "../../context/TeamProgressContext";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import dayjs from "dayjs";
import {useEffect} from "react";

/**
 * Lessons Completed Chart Component
 * 
 * Matches legacy LessonsCompletedChart exactly:
 * - 394px height container with border
 * - Orange gradient area fill
 * - Custom axis formatting
 * - Catmull-Rom curve
 */
export function LessonsCompletedChart() {
	const theme = useTheme();
	const {t} = useSSTranslation();
	const {updateSectionState} = useTeamProgressContext();
	const {chartData, isLoading, isError} = useChart();

	useEffect(() => {
		updateSectionState(isLoading, "chart");
	}, [isLoading, updateSectionState]);

	if (isLoading) {
		return (
			<Container gap={3}>
				<Typography variant="h6" color="#F9F9F9B2">
					{t("team_progress_widget_completedLessons", "Completed Lessons")}
				</Typography>
				<Skeleton variant="rectangular" width="100%" height={370} sx={{borderRadius: 2}} />
			</Container>
		);
	}

	if (isError || !chartData) {
		return (
			<Container gap={3}>
				<Typography variant="h6" color="#F9F9F9B2">
					{t("team_progress_widget_completedLessons", "Completed Lessons")}
				</Typography>
				<Box
					sx={{
						height: 370,
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					<Typography color="error">Failed to load chart data</Typography>
				</Box>
			</Container>
		);
	}

	// Transform data for MUI X Charts
	const xAxisData = chartData.data.map((point) => point.date);
	const yAxisData = chartData.data.map((point) => point.lessonsCompleted);
	const isDaily = chartData.axisDataType === "day";

	return (
		<Container gap={3}>
			<Typography variant="h6" color="#F9F9F9B2">
				{t("team_progress_widget_completedLessons", "Completed Lessons")}
			</Typography>
			<LineChart
				xAxis={[{
					dataKey: "date",
					valueFormatter: (value: any) => dayjs(value[isDaily ? 0 : 1]).format(isDaily ? "MMM D" : "MMM"),
					tickInterval: (value: any) => chartData.tickInterval.includes(value[isDaily ? 0 : 1]),
					scaleType: "point"
				}]}
				series={[
					{
						dataKey: "lessonsCompleted",
						curve: "catmullRom",
						area: true,
						color: theme.palette.primary.main,
						showMark: false
					}
				]}
				dataset={chartData.data as any}
				height={370}
				tooltip={{
					trigger: "axis"
				}}
				sx={{
					"& .MuiAreaElement-root": {
						fill: "url(#areaGradient)"
					}
				}}
			>
				<linearGradient id="areaGradient" x1="100%" y1="0%" x2="100%" y2="100%">
					<stop offset="0" stopColor="#C27A08" />
					<stop offset="1" stopColor="#141414" />
				</linearGradient>
			</LineChart>
		</Container>
	);
}

const Container = styled(Box)(() => ({
	borderRadius: "8px",
	height: "394px",
	backgroundColor: palette.greyDark,
	border: "solid 1px #A1A1A026",
	padding: "25px 15px"
}));
